import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	inject,
	NgZone,
	viewChild,
} from "@angular/core";

interface Sparkle {
	x: number;
	y: number;
	/** Speed in px/ms. */
	vx: number;
	vy: number;
	age: number;
	lifetime: number;
	radius: number;
	color: string;
	/** Offsets the twinkle so sparkles born together do not pulse in step. */
	phase: number;
}

/** Saturated enough to show on the light theme's near-white pages as well as the dark theme. */
const SPARKLE_COLORS = ["#f5b301", "#ff3d6e", "#18c26b", "#2f9bff", "#c25cff"];
const MAX_SPARKLES = 150;
/** One sparkle per this many pixels the cursor travels, at most three per move. */
const PIXELS_PER_SPARKLE = 12;
/** Downward pull in px/ms². */
const GRAVITY = 0.00018;

/**
 * Sparkles that trail the mouse cursor and drift down as they fade. Drawn on a canvas outside
 * Angular's zone, and the animation loop only runs while sparkles are on screen.
 */
@Component({
	selector: "app-cursor-sparkles",
	template: `
		<canvas
			#canvas
			class="cursor-sparkles"></canvas>
	`,
	styles: `
		.cursor-sparkles {
			position: fixed;
			inset: 0;
			width: 100vw;
			height: 100vh;
			pointer-events: none;
			z-index: 10000;
		}
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CursorSparklesComponent {
	private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>("canvas");
	private readonly zone = inject(NgZone);
	private context: CanvasRenderingContext2D | null = null;
	private sparkles: Sparkle[] = [];
	private frame: number | null = null;
	private lastFrameTime = 0;
	private lastPointer: { x: number; y: number } | null = null;

	constructor() {
		afterNextRender(() => this.start());
		inject(DestroyRef).onDestroy(() => this.stop());
	}

	private start(): void {
		if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
			return;
		}
		this.context = this.canvas().nativeElement.getContext("2d");
		if (!this.context) {
			return;
		}
		this.resize();
		this.zone.runOutsideAngular(() => {
			window.addEventListener("pointermove", this.onPointerMove, { passive: true });
			window.addEventListener("resize", this.resize);
		});
	}

	private stop(): void {
		window.removeEventListener("pointermove", this.onPointerMove);
		window.removeEventListener("resize", this.resize);
		if (this.frame !== null) {
			cancelAnimationFrame(this.frame);
			this.frame = null;
		}
	}

	private readonly resize = (): void => {
		const canvas = this.canvas().nativeElement;
		const ratio = window.devicePixelRatio || 1;
		canvas.width = window.innerWidth * ratio;
		canvas.height = window.innerHeight * ratio;
		this.context?.setTransform(ratio, 0, 0, ratio, 0, 0);
	};

	private readonly onPointerMove = (event: PointerEvent): void => {
		// A finger drags the page; only a mouse pointer leaves a trail
		if (event.pointerType !== "mouse") {
			return;
		}
		const previous = this.lastPointer ?? { x: event.clientX, y: event.clientY };
		this.lastPointer = { x: event.clientX, y: event.clientY };
		const distance = Math.hypot(event.clientX - previous.x, event.clientY - previous.y);
		const count = Math.min(3, Math.max(1, Math.round(distance / PIXELS_PER_SPARKLE)));
		for (let i = 0; i < count && this.sparkles.length < MAX_SPARKLES; i++) {
			this.sparkles.push(createSparkle(event.clientX, event.clientY));
		}
		if (this.frame === null) {
			this.lastFrameTime = performance.now();
			this.frame = requestAnimationFrame(this.tick);
		}
	};

	private readonly tick = (time: number): void => {
		// Capped so a tab coming back from the background does not jump every sparkle at once
		const elapsed = Math.min(time - this.lastFrameTime, 50);
		this.lastFrameTime = time;
		this.sparkles = this.sparkles.filter((sparkle) => {
			sparkle.age += elapsed;
			sparkle.vy += GRAVITY * elapsed;
			sparkle.x += sparkle.vx * elapsed;
			sparkle.y += sparkle.vy * elapsed;
			return sparkle.age < sparkle.lifetime;
		});
		this.draw();
		this.frame = this.sparkles.length > 0 ? requestAnimationFrame(this.tick) : null;
	};

	private draw(): void {
		const context = this.context;
		if (!context) {
			return;
		}
		context.clearRect(0, 0, window.innerWidth, window.innerHeight);
		for (const sparkle of this.sparkles) {
			const life = 1 - sparkle.age / sparkle.lifetime;
			const twinkle = 0.65 + 0.35 * Math.sin(sparkle.age / 70 + sparkle.phase);
			context.globalAlpha = life;
			context.fillStyle = sparkle.color;
			context.shadowColor = sparkle.color;
			context.shadowBlur = 6;
			drawStar(context, sparkle.x, sparkle.y, sparkle.radius * twinkle);
		}
		context.globalAlpha = 1;
	}
}

function createSparkle(x: number, y: number): Sparkle {
	return {
		x: x + randomBetween(-4, 4),
		y: y + randomBetween(-4, 4),
		vx: randomBetween(-0.03, 0.03),
		vy: randomBetween(-0.06, 0),
		age: 0,
		lifetime: randomBetween(700, 1300),
		radius: randomBetween(3.5, 7),
		color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
		phase: Math.random() * Math.PI * 2,
	};
}

/** A four-pointed star, its waist at a quarter of its radius. */
function drawStar(context: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
	const waist = radius * 0.25;
	context.beginPath();
	context.moveTo(x, y - radius);
	context.lineTo(x + waist, y - waist);
	context.lineTo(x + radius, y);
	context.lineTo(x + waist, y + waist);
	context.lineTo(x, y + radius);
	context.lineTo(x - waist, y + waist);
	context.lineTo(x - radius, y);
	context.lineTo(x - waist, y - waist);
	context.closePath();
	context.fill();
}

function randomBetween(min: number, max: number): number {
	return min + Math.random() * (max - min);
}
